import {
  lazy,
  Suspense,
  ComponentType,
  Component,
  ErrorInfo,
  ReactNode,
} from 'react';
import { CircularProgress, Box, Button, Typography } from '@mui/material';

// Loading component
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '200px',
      width: '100%',
    }}
  >
    <CircularProgress size={24} sx={{ color: '#10b981' }} />
  </Box>
);

// Error boundary for lazy loaded components
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class LazyLoadErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Lazy load error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            padding: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" gutterBottom>
            Failed to load component
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {this.state.error?.message ||
              'An error occurred while loading this page'}
          </Typography>
          <Button variant="contained" onClick={this.handleRetry}>
            Retry
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

/**
 * Detects the browser's error for a dynamic import that 404s or otherwise
 * can't be fetched — the signature of a stale chunk hash after a new deploy.
 * Retrying the same import again won't help here; only a full reload
 * (which re-fetches index.html and its current chunk manifest) will.
 */
function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Loading chunk .* failed/i.test(message) ||
    /Importing a module script failed/i.test(message) // Safari's wording for the same failure
  );
}

const STALE_CHUNK_RELOAD_KEY = 'lazyload-stale-chunk-reload';

/**
 * If we hit a stale-chunk error, force exactly one full reload to pick up
 * the current build. The sessionStorage flag prevents an infinite reload
 * loop if the reload doesn't fix it (e.g. genuine network outage, or the
 * deploy itself is broken) — in that case we fall through to the retry
 * logic / error boundary instead of reloading forever.
 */
function handleStaleChunk(): boolean {
  const alreadyTried = sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY);
  if (alreadyTried) return false;

  sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, '1');
  window.location.reload();
  return true;
}

/**
 * Retry logic for failed imports
 * Retries up to 3 times with exponential backoff for transient failures.
 * Stale-chunk failures (post-deploy hash mismatch) bypass retry entirely
 * and trigger a single full-page reload instead, since retrying the same
 * dead URL can never succeed.
 */
function retryImport<T>(
  importFunc: () => Promise<T>,
  retries = 3,
  delay = 1000
): Promise<T> {
  return importFunc()
    .then((module) => {
      // a chunk loaded: the next deploy may again reload once to recover
      try {
        sessionStorage.removeItem(STALE_CHUNK_RELOAD_KEY);
      } catch {
        // storage blocked: nothing to clear
      }
      return module;
    })
    .catch((error) => {
    if (isStaleChunkError(error)) {
      const reloaded = handleStaleChunk();
      if (reloaded) {
        // Reload is already in flight; return a never-resolving promise so
        // React doesn't render the error boundary during the brief window
        // before the page actually navigates away.
        return new Promise<T>(() => {});
      }
      // Already tried reloading once this session and it's still failing —
      // fall through to normal retry/error-boundary handling below.
    }

    if (retries === 0) {
      throw error;
    }

    return new Promise<T>((resolve) => {
      setTimeout(() => {
        resolve(retryImport(importFunc, retries - 1, delay * 2));
      }, delay);
    });
  });
}

/**
 * Enhanced utility function to lazy load components with Suspense, retry logic, and error boundary
 * @param importFunc - Dynamic import function
 * @param options - Configuration options
 * @returns Component wrapped with Suspense and error boundary
 *
 * @example
 * const Dashboard = lazyLoad(() => import('@/views/dashboard'));
 * const Settings = lazyLoad(() => import('@/views/settings'), { retries: 5 });
 */
export function lazyLoad<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  options?: {
    fallback?: ReactNode;
    errorFallback?: ReactNode;
    retries?: number;
    preload?: boolean;
  }
) {
  const {
    fallback,
    errorFallback,
    retries = 3,
    preload = false,
  } = options || {};

  // Optionally preload the component
  if (preload) {
    retryImport(importFunc, retries);
  }

  // Create lazy component once outside the returned component
  const LazyComponent = lazy(() => retryImport(importFunc, retries));

  // Return a named component to help with debugging and prevent remounting
  const LazyWrapper = (props: React.ComponentProps<T>) => (
    <LazyLoadErrorBoundary fallback={errorFallback}>
      <Suspense fallback={fallback || <LoadingFallback />}>
        <LazyComponent {...props} />
      </Suspense>
    </LazyLoadErrorBoundary>
  );

  return LazyWrapper;
}

/**
 * Preload a lazy component
 * Useful for prefetching on hover or other interactions
 *
 * @example
 * const Dashboard = lazyLoad(() => import('@/views/dashboard'));
 * <Button onMouseEnter={() => preloadComponent(() => import('@/views/dashboard'))}>
 */
export function preloadComponent<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  retries = 3
) {
  return retryImport(importFunc, retries);
}

export default lazyLoad;
